import type { RafflePaymentPayload } from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RafflePaymentPage/RafflePaymentPage';
import type { EntryFormValues } from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RaffleForms/RaffleEntryForm';
import type { GiveawayConfirmationState } from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RafflePaymentPage/RafflePaymentConfirmationPage';
import type { PackItem } from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RafflePage';
import {
  getFirstMonthDiscountedPrice,
  isDiscountEligibleSubscriptionPackage
} from '../../utils/subscriptionDiscounts';
import { getGiveawayOfferDraft } from './giveawayOfferFlowState';
import type { GiveawayOfferDraft } from './giveawayOfferFlowState';

/* Turning a QR signup into a subscription checkout, and its result into the
 * confirmation screen the giveaway funnel already has.
 *
 * Both halves are modelled on the funnel next door —
 * `features/PartnerSignup/partnerPaymentPayload.ts` for the payload, and
 * `PartnerCheckoutDialog.savePartnerPurchaseDraft` for the confirmation state.
 * Same two jobs, same contracts, so the three flows stay comparable.
 */

/** What the buyer is actually charged today, discount included. */
export const displayedSubscriptionPrice = (pack: PackItem) => {
  const price = Number(pack.price || 0);
  return isDiscountEligibleSubscriptionPackage(pack.id)
    ? getFirstMonthDiscountedPrice(price)
    : price;
};

/* The "START WITH n ENTRIES FROM THE GET GO" figure the cards advertise.
 *
 * `entries x 10`, which is duplicated across five files under five names (see
 * the CODEMAP note on SubscriptionUpgradeCards). It is repeated here rather than
 * skipped for a reason that is not cosmetic: the cards on the confirmation page
 * PROMISE this bonus, so a checkout that did not send it would charge someone for
 * an entry count they were shown and do not receive. It must agree with the
 * server and with the other four copies. */
export const startingBonusEntries = (pack: PackItem) =>
  Math.max(0, Math.floor(Number(pack.entries || 0) * 10));

/* What the checkout's details step starts from when the draft is not a whole
 * payload. Since 2026-10-07 the sign-up asks for no phone, country or state, so
 * the draft it leaves has a name, a username and an email and nothing else;
 * the form then asks for the rest with these already typed in. Only values that
 * are actually there are passed, so the form's own defaults (Australia, an empty
 * number) stand for anything missing. */
export const draftEntryPrefill = (
  draft: GiveawayOfferDraft | null
): Partial<EntryFormValues> | undefined => {
  if (!draft) return undefined;
  const firstName = (draft.firstName || '').trim();
  const lastName = (draft.lastName || '').trim();
  const out: Partial<EntryFormValues> = {};
  if (firstName) out.firstName = firstName;
  if (lastName) out.lastName = lastName;
  if (firstName || lastName) out.fullName = `${firstName} ${lastName}`.trim();
  if (draft.username) out.username = draft.username;
  if (draft.email) out.email = draft.email.trim().toLowerCase();
  if (draft.countryIso2) {
    out.countryIso2 = draft.countryIso2;
    out.countryName = draft.countryName ?? '';
    out.stateIso2 = draft.stateIso2 ?? '';
    out.stateName = draft.stateName ?? '';
  }
  if (draft.phone) {
    out.phone = draft.phone;
    if (draft.phoneCountryCode) out.phoneCountryCode = draft.phoneCountryCode;
  }
  if (draft.eligible) out.eligible = draft.eligible;
  return Object.keys(out).length ? out : undefined;
};

/** The signup details as Stripe's form wants them, or null if we have no draft.
 *
 * Null ALSO for a draft with no phone or no country — the trimmed QR sign-up's
 * (2026-10-07): a payload built from it would carry an empty phone that the
 * server refuses at `ensureCustomerAndUser`, so the checkout asks for the rest
 * first (`RaffleEntryForm`, pre-filled by `draftEntryPrefill`). */
export const buildGiveawayOfferPaymentPayload = ({
  selectedPack,
  subscriptionItems = []
}: {
  selectedPack: PackItem;
  subscriptionItems?: PackItem[];
}): RafflePaymentPayload | null => {
  const draft = getGiveawayOfferDraft();
  if (!draft?.email || !draft.phone || !draft.countryIso2) return null;

  const firstName = (draft.firstName || '').trim();
  const lastName = (draft.lastName || '').trim();
  const bonus = startingBonusEntries(selectedPack);

  return {
    entry: {
      fullName: `${firstName} ${lastName}`.trim(),
      firstName,
      lastName,
      username: draft.username ?? '',
      email: (draft.email ?? '').trim().toLowerCase(),
      phone: draft.phone ?? '',
      countryIso2: draft.countryIso2 ?? '',
      countryName: draft.countryName ?? '',
      stateIso2: draft.stateIso2 ?? '',
      stateName: draft.stateName ?? '',
      phoneCountryCode: draft.phoneCountryCode ?? '',
      agreeToTerms: Boolean(draft.agreeToTerms),
      eligible: draft.eligible ?? ''
    },
    selectedPack,
    standaloneItems: [],
    subscriptionItems: subscriptionItems.length
      ? subscriptionItems
      : [selectedPack],
    signupCodeBonusEntries: bonus > 0 ? bonus : undefined,
    signupCodeBonusSource: bonus > 0 ? 'giveaway_subs' : undefined,
    /* What tells the server this purchase came from the poster funnel. The
     * account itself already carries `signupSource` from signup, so this is
     * belt-and-braces for the case where checkout creates it. */
    signupSource: 'giveaway-offer',
    raffleUnlockToken: null,
    hasActiveSubscription: false
  };
};

/* Hand the purchase to `/giveaway-subs/confirmation`.
 *
 * That page reads this exact sessionStorage key and renders it, so reusing it is
 * what the admin asked for — "the same congratulations page we already did" —
 * without a fourth confirmation screen to keep in step with the other three. */
export const saveGiveawayOfferConfirmationDraft = ({
  payload,
  pack,
  paymentIntentId,
  accountCreated
}: {
  payload: RafflePaymentPayload;
  pack: PackItem;
  paymentIntentId?: string;
  accountCreated?: boolean;
}) => {
  const bonus = startingBonusEntries(pack);

  const state: GiveawayConfirmationState = {
    entry: payload.entry,
    selectedPack: pack,
    totalEntries: Number(pack.entries || 0) + bonus,
    nftRewards: Number(pack.nfts || 0),
    amountPaidAud: displayedSubscriptionPrice(pack),
    checkoutMode: 'subscription',
    incPerMonth: Number(pack.tokens || 0),
    /* Whether to greet this buyer as brand new. Everyone arriving here signed up
     * on the offer page moments ago, so the checkout FINDS their account rather
     * than creating one and `accountCreated` is false — which is the honest
     * answer: no "your login details have been emailed" line is shown, because
     * no such email is sent on this path. */
    isExistingVerifiedUser:
      accountCreated !== undefined ? !accountCreated : true,
    memberPortalPath: '/login',
    bonusSelected: false,
    paymentIntentId
  };

  try {
    sessionStorage.setItem('giveawayConfirmationState', JSON.stringify(state));
  } catch {
    /* The confirmation page renders its own empty state if this is missing. */
  }
};
